/**
 * Deferred deep link — data layer (feat #177).
 *
 * Thin typed wrapper over `apiClient` for the deferred deep link endpoints:
 *
 *   POST   /api/v1/deferred-deep-link/resolve          — createDeferredLink
 *   GET    /api/v1/deferred-deep-link/links            — listLinks
 *   GET    /api/v1/deferred-deep-link/links/:id        — getLink
 *   PATCH  /api/v1/deferred-deep-link/links/:id        — updateLink
 *   DELETE /api/v1/deferred-deep-link/links/:id        — deleteLink
 *   POST   /api/v1/deferred-deep-link/claim            — claimLink (SDK use)
 *   GET    /api/v1/deferred-deep-link/claims/:id       — listClaims
 *   GET    /api/v1/deferred-deep-link/stats            — statsWindow
 *
 * Pure helpers (validators, formatters, query keys, state machine) live
 * here so they can be unit-tested without a DOM or a TanStack context.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Row shapes — mirror the server response exactly.
// ---------------------------------------------------------------------------

export type DeferredPlatform = "ios" | "android" | "web" | "unknown";

export interface DeferredDeepLink {
	id: string;
	tenant_id: string;
	target_url: string;
	deep_link_path: string | null;
	campaign_id: string | null;
	fingerprint_hash: string;
	ip_hash: string | null;
	ua: string | null;
	ua_hash: string | null;
	platform: DeferredPlatform;
	context: string;
	ttl_expires_at: number;
	claimed_at: number | null;
	claimed_count: number;
	claim_token: string;
	enabled: number;
	created_at: number;
	updated_at: number;
}

export interface DeferredDeepLinkClaim {
	id: string;
	tenant_id: string;
	link_id: string;
	claim_at: number;
	install_identifier: string;
	ip_hash: string | null;
	ua_hash: string | null;
	metadata: string;
}

export interface DeferredStats {
	window_start: number;
	window_end: number;
	totals: {
		links: number;
		live: number;
		expired: number;
		claims: number;
		unique_installs: number;
		resolution_rate: number;
	};
	per_platform: Array<{
		platform: DeferredPlatform;
		links: number;
		claims: number;
	}>;
}

export interface ListLinksResponse {
	links: DeferredDeepLink[];
}

export interface LinkResponse {
	link: DeferredDeepLink;
}

export interface ListClaimsResponse {
	claims: DeferredDeepLinkClaim[];
}

export interface ClaimResponse {
	claim: DeferredDeepLinkClaim;
	link: DeferredDeepLink;
	idempotent: boolean;
}

export interface StatsResponse {
	stats: DeferredStats;
}

// ---------------------------------------------------------------------------
// Validators — mirror the server contract so the UI can short-circuit
// obvious errors before the round-trip.
// ---------------------------------------------------------------------------

const ID_RE = /^[A-Za-z0-9_.:-]{1,128}$/;
const FINGERPRINT_RE = /^[A-Za-z0-9_.:-]{6,256}$/;
const TOKEN_RE = /^[A-Za-z0-9_-]{16,128}$/;

export const PLATFORMS: readonly DeferredPlatform[] = [
	"ios",
	"android",
	"web",
	"unknown",
];

export function validateId(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (!ID_RE.test(trimmed)) return null;
	return trimmed;
}

export function validateFingerprint(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (!FINGERPRINT_RE.test(trimmed)) return null;
	return trimmed;
}

export function validateToken(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (!TOKEN_RE.test(trimmed)) return null;
	return trimmed;
}

export function validatePlatform(raw: string): DeferredPlatform | null {
	if (typeof raw !== "string") return null;
	const lower = raw.trim().toLowerCase();
	if ((PLATFORMS as readonly string[]).includes(lower)) {
		return lower as DeferredPlatform;
	}
	return null;
}

export function validateTargetUrl(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > 2048) return null;
	try {
		const url = new URL(trimmed);
		if (url.protocol !== "http:" && url.protocol !== "https:") return null;
		return trimmed;
	} catch {
		return null;
	}
}

export function validateDeepLinkPath(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim();
	if (trimmed.length === 0 || trimmed.length > 1024) return null;
	// eslint-disable-next-line no-control-regex
	if (/[\u0000-\u001f\u007f]/.test(trimmed)) return null;
	return trimmed;
}

export function validateTtlSeconds(raw: number | string): number | null {
	const n = typeof raw === "string" ? Number(raw) : raw;
	if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
	if (n < 60 || n > 90 * 24 * 60 * 60) return null;
	return n;
}

// ---------------------------------------------------------------------------
// Formatters — pure, DOM-free, deterministic.
// ---------------------------------------------------------------------------

export function formatUnixSec(unixSec: number | null | undefined): string {
	if (unixSec == null) return "—";
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function formatDateOnly(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	return new Date(unixSec * 1000).toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

export function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "—";
	const d = Math.floor(seconds / 86_400);
	const h = Math.floor((seconds % 86_400) / 3_600);
	const m = Math.floor((seconds % 3_600) / 60);
	if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
	if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
	return `${m}m`;
}

export function formatPercent(ratio: number, digits = 1): string {
	if (!Number.isFinite(ratio)) return "—";
	return `${(ratio * 100).toFixed(digits)}%`;
}

export type LinkStatus = "disabled" | "expired" | "claimed" | "live";

export function statusOf(
	link: DeferredDeepLink,
	nowSec: number = Math.floor(Date.now() / 1000),
): LinkStatus {
	if (link.enabled !== 1) return "disabled";
	if (link.ttl_expires_at <= nowSec) return "expired";
	if (link.claimed_count > 0) return "claimed";
	return "live";
}

export function timeUntilExpiry(
	link: DeferredDeepLink,
	nowSec: number = Math.floor(Date.now() / 1000),
): number {
	return Math.max(0, link.ttl_expires_at - nowSec);
}

/**
 * Best-effort JSON parse of the stored context blob. Always returns a
 * plain record (possibly empty). Never throws.
 */
export function parseContext(raw: string | null | undefined): Record<string, unknown> {
	if (typeof raw !== "string" || raw.length === 0) return {};
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
			return parsed as Record<string, unknown>;
		}
	} catch {
		/* swallow — bad context becomes empty */
	}
	return {};
}

// ---------------------------------------------------------------------------
// Query keys — grouped for ergonomic invalidation.
// ---------------------------------------------------------------------------

export const deferredDeepLinkQueryKeys = {
	all: ["deferred-deep-link"] as const,
	links: (opts?: { fingerprint_hash?: string; campaign_id?: string }) => {
		const base = [...deferredDeepLinkQueryKeys.all, "links"] as const;
		if (!opts || (!opts.fingerprint_hash && !opts.campaign_id)) return base;
		return [
			...base,
			{
				fingerprint_hash: opts.fingerprint_hash ?? null,
				campaign_id: opts.campaign_id ?? null,
			},
		] as const;
	},
	link: (id: string) =>
		[...deferredDeepLinkQueryKeys.all, "link", id] as const,
	claims: (linkId: string) =>
		[...deferredDeepLinkQueryKeys.all, "claims", linkId] as const,
	stats: (windowStart?: number, windowEnd?: number) =>
		[
			...deferredDeepLinkQueryKeys.all,
			"stats",
			{ windowStart: windowStart ?? null, windowEnd: windowEnd ?? null },
		] as const,
};

// ---------------------------------------------------------------------------
// Remote bindings — thin `apiClient` shims.
// ---------------------------------------------------------------------------

const BASE = "/deferred-deep-link";

export interface CreateDeferredLinkArgs {
	target_url: string;
	deep_link_path?: string | null;
	campaign_id?: string | null;
	fingerprint_hash?: string;
	platform?: DeferredPlatform;
	ttl_seconds?: number;
	context?: Record<string, unknown> | null;
	enabled?: boolean;
}

export async function createDeferredLink(
	args: CreateDeferredLinkArgs,
): Promise<DeferredDeepLink> {
	const payload: Record<string, unknown> = {
		target_url: args.target_url,
	};
	if (args.deep_link_path != null) payload.deep_link_path = args.deep_link_path;
	if (args.campaign_id != null) payload.campaign_id = args.campaign_id;
	if (args.fingerprint_hash !== undefined) {
		payload.fingerprint_hash = args.fingerprint_hash;
	}
	if (args.platform !== undefined) payload.platform = args.platform;
	if (args.ttl_seconds !== undefined) payload.ttl_seconds = args.ttl_seconds;
	if (args.context != null) payload.context = args.context;
	if (args.enabled !== undefined) payload.enabled = args.enabled;
	const res = await apiClient.post<LinkResponse>(`${BASE}/resolve`, payload);
	return res.link;
}

export interface ListLinksArgs {
	fingerprint_hash?: string;
	campaign_id?: string;
	since?: number;
	limit?: number;
}

export async function listLinks(
	args: ListLinksArgs = {},
): Promise<DeferredDeepLink[]> {
	const query: Record<string, string | number> = {};
	if (args.fingerprint_hash) query.fingerprint_hash = args.fingerprint_hash;
	if (args.campaign_id) query.campaign_id = args.campaign_id;
	if (args.since !== undefined) query.since = args.since;
	if (args.limit !== undefined) query.limit = args.limit;
	const res = await apiClient.get<ListLinksResponse>(`${BASE}/links`, {
		query: Object.keys(query).length ? query : undefined,
	});
	return res.links;
}

export async function getLink(id: string): Promise<DeferredDeepLink> {
	const res = await apiClient.get<LinkResponse>(
		`${BASE}/links/${encodeURIComponent(id)}`,
	);
	return res.link;
}

export interface UpdateLinkArgs {
	id: string;
	enabled?: boolean;
	extend_ttl_seconds?: number;
	ttl_expires_at?: number;
}

export async function updateLink(args: UpdateLinkArgs): Promise<DeferredDeepLink> {
	const payload: Record<string, unknown> = {};
	if (args.enabled !== undefined) payload.enabled = args.enabled;
	if (args.extend_ttl_seconds !== undefined) {
		payload.extend_ttl_seconds = args.extend_ttl_seconds;
	}
	if (args.ttl_expires_at !== undefined) {
		payload.ttl_expires_at = args.ttl_expires_at;
	}
	const res = await apiClient.patch<LinkResponse>(
		`${BASE}/links/${encodeURIComponent(args.id)}`,
		payload,
	);
	return res.link;
}

export async function deleteLink(
	id: string,
): Promise<{ ok: true; id: string }> {
	return apiClient.delete<{ ok: true; id: string }>(
		`${BASE}/links/${encodeURIComponent(id)}`,
	);
}

export interface ClaimArgs {
	install_identifier: string;
	claim_token?: string;
	fingerprint_hash?: string;
	hints?: readonly string[];
	metadata?: Record<string, unknown> | null;
}

export async function claimLink(args: ClaimArgs): Promise<ClaimResponse> {
	const payload: Record<string, unknown> = {
		install_identifier: args.install_identifier,
	};
	if (args.claim_token !== undefined) payload.claim_token = args.claim_token;
	if (args.fingerprint_hash !== undefined) {
		payload.fingerprint_hash = args.fingerprint_hash;
	}
	if (args.hints !== undefined) payload.hints = args.hints;
	if (args.metadata != null) payload.metadata = args.metadata;
	return apiClient.post<ClaimResponse>(`${BASE}/claim`, payload);
}

export async function listClaims(
	linkId: string,
	limit?: number,
): Promise<DeferredDeepLinkClaim[]> {
	const res = await apiClient.get<ListClaimsResponse>(
		`${BASE}/claims/${encodeURIComponent(linkId)}`,
		{ query: limit ? { limit } : undefined },
	);
	return res.claims;
}

export interface StatsWindow {
	window_start?: number;
	window_end?: number;
}

export async function statsWindow(
	args: StatsWindow = {},
): Promise<DeferredStats> {
	const query: Record<string, number> = {};
	if (args.window_start !== undefined) query.window_start = args.window_start;
	if (args.window_end !== undefined) query.window_end = args.window_end;
	const res = await apiClient.get<StatsResponse>(`${BASE}/stats`, {
		query: Object.keys(query).length ? query : undefined,
	});
	return res.stats;
}
